/**
 * Task Status Checker
 *
 * ใช้สำหรับเช็กสถานะของ task ที่ใช้เวลานาน (เช่น Sora 2)
 * โดยใช้ taskId ที่เก็บไว้ใน generation_logs
 */

import { supabase } from "@/integrations/supabase/client";
import { pollKieTask, getKieApiKey } from "./kie-service";

export async function checkTaskStatus(logId: string) {
  try {
    // 1. ดึงข้อมูล log
    const { data: log, error: logError } = await supabase
      .from("generation_logs")
      .select("*")
      .eq("id", logId)
      .single();

    if (logError || !log) {
      throw new Error("Log not found");
    }

    // 2. ตรวจสอบว่ามี taskId หรือไม่ (check both result_url and task_id columns)
    const taskId = log.result_url || log.task_id;

    // DEBUG
    // console.log(`🧐 Checking task for log ${logId}: status=${log.status}, result_url=${log.result_url}, task_id=${log.task_id}`);

    if (!taskId) {
      // If status is already failed, just return the failed status
      if (log.status === "failed") {
        return {
          success: false,
          status: "failed",
          message: log.error_message || "Task failed at submission. Credits have been refunded."
        };
      }

      // If no taskId and not yet failed, mark as failed and refund credits
      console.log(`⚠️ No taskId found for log ${logId}, marking as failed and refunding credits`);

      // Update status to failed
      const { error: updateError } = await (supabase.rpc as any)('update_generation_log_status', {
        p_log_id: logId,
        p_user_id: log.user_id,
        p_status: "failed",
        p_result_url: null,
        p_completed_at: new Date().toISOString()
      });

      if (updateError) {
        // Fallback to direct update
        await supabase
          .from("generation_logs")
          .update({
            status: "failed",
            error_message: "No task ID found - task failed at submission"
          })
          .eq("id", logId);
      }

      // Refund credits
      const { error: refundError } = await supabase.rpc('refund_credits_for_failed_generation' as any, {
        p_log_id: logId
      });

      if (refundError) {
        console.error("Failed to refund credits:", refundError);
      }

      return {
        success: false,
        status: "failed",
        message: "Task failed at submission - no task ID was created. Credits have been refunded."
      };
    }

    // ถ้าเป็น URL แสดงว่าสร้างเสร็จแล้ว ไม่ต้องเช็กสถานะ
    if (taskId.startsWith("http")) {
      return {
        success: true,
        status: "completed",
        url: taskId,
        message: "Already completed"
      };
    }

    // 3. เรียก KIE API เพื่อเช็กสถานะ
    const apiKey = await getKieApiKey();

    // Determine pollType based on model
    let pollType: "modify" | "market" | "suno" | "veo" = "modify";
    const model = log.model?.toLowerCase() || "";

    if (model.includes("veo")) {
      pollType = "veo";
    } else if (model.includes("seedance") || model.includes("sora") || model.includes("wan") || model.includes("luma") || model.includes("seedream") || model.includes("elevenlabs")) {
      pollType = "market";
    } else if (model.includes("suno") || model.includes("music")) {
      pollType = "suno";
    } else if (log.model === "AI Video Pro" || log.model === "AI Movie") {
      pollType = "modify";
    }

    const pollResult = await pollKieTask(taskId, apiKey, pollType);

    // DEBUG: Log the full response
    // console.log(`🔍 KIE API Response (${pollType}):`, JSON.stringify(pollResult, null, 2));

    // 4. ดึงข้อมูลสถานะและ URL ตาม API doc
    let status = pollType === "market"
      ? (pollResult.state ?? pollResult.data?.state)
      : pollType === "veo"
        ? (pollResult.data?.successFlag === 1 ? "success" :
          pollResult.data?.successFlag === 2 || pollResult.data?.successFlag === 3 ? "failed" : "processing")
        : (pollResult.status ?? pollResult.data?.status ?? pollResult.data?.taskStatus);

    let url = null;

    // console.log('📊 Raw status from API:', status, 'Type:', typeof status);

    // Normalize status to string if it's a number
    if (typeof status === 'number') {
      if (status === 3) status = "success";
      else if (status === 4) status = "failed";
      else status = "processing";
    } else if (typeof status === 'string') {
      status = status.toLowerCase();
    }

    // console.log('🔄 Normalized status:', status);

    if (pollType === "market") {
      // Market models put result in resultJson
      const resultJson = pollResult.resultJson || pollResult.data?.resultJson;
      if (resultJson) {
        try {
          const parsed = typeof resultJson === 'string' ? JSON.parse(resultJson) : resultJson;
          url = parsed.resultUrls?.[0] || parsed.url || parsed.audio_url || parsed.audioUrl || (Array.isArray(parsed) ? parsed[0] : null);
          if (!url && typeof parsed === 'string' && parsed.startsWith('http')) url = parsed;
        } catch (e) {
          if (typeof resultJson === 'string' && resultJson.startsWith('http')) url = resultJson;
        }
      }
    } else if (pollType === "veo") {
      url = pollResult.data?.response?.resultUrls?.[0];
    } else if (pollType === "suno") {
      // Suno specific extraction
      const sunoData = pollResult.data || pollResult;
      const response = sunoData.response || sunoData.result;

      // Check for sunoData array first (new format)
      if (response?.sunoData && Array.isArray(response.sunoData) && response.sunoData.length > 0) {
        url = response.sunoData
          .map((item: any) => item.audioUrl || item.streamAudioUrl || item.audio_url || item.url)
          .filter((u: any) => typeof u === "string" && u.startsWith("http"))
          .join(",");
      } else if (Array.isArray(response?.data) && response.data.length > 0) {
        url = response.data
          .map((item: any) => item.audio_url || item.audioUrl || item.stream_audio_url || item.streamAudioUrl)
          .filter((u: any) => typeof u === "string" && u.startsWith("http"))
          .join(",");
      } else if (Array.isArray(response) && response.length > 0) {
        url = response
          .map((item: any) => item.audioUrl || item.streamAudioUrl || item.audio_url || item.url || item.video_url)
          .filter((u: any) => typeof u === "string" && u.startsWith("http"))
          .join(",");
      } else if (response && typeof response === 'object') {
        url = response.audioUrl || response.streamAudioUrl || response.audio_url || response.url || response.video_url;
      }
    }

    // ถ้าไม่มี URL จากการดึงข้อมูลเฉพาะประเภท ให้ลองหาจากที่อื่น
    if (!url) {
      url = pollResult.url || pollResult.data?.url || pollResult.resultUrl || pollResult.data?.resultUrl || pollResult.audio_url || pollResult.data?.audio_url || pollResult.audioUrl || pollResult.data?.audioUrl || pollResult.data?.response?.audio_url;
    }

    // console.log('🔗 Final URL:', url);

    // 5. อัพเดตสถานะใน database
    if (status === "success" || status === "completed" || url) {
      // ป้องกันกรณี url เป็นค่าว่าง
      if (!url && status === "success") {
        console.warn("⚠️ Status is success but no URL found yet");
        return {
          success: true,
          status: "processing",
          message: "Success but waiting for URL..."
        };
      }

      // Use RPC to update log status to bypass RLS issues
      const { error: updateError } = await (supabase.rpc as any)('update_generation_log_status', {
        p_log_id: logId,
        p_user_id: log.user_id,
        p_status: "completed",
        p_result_url: url,
        p_completed_at: new Date().toISOString()
      });

      if (updateError) {
        console.error("RPC update failed, trying direct update fallback...", updateError);
        // Fallback to direct update
        await supabase
          .from("generation_logs")
          .update({
            status: "completed",
            result_url: url
          })
          .eq("id", logId);
      }

      return {
        success: true,
        status: "completed",
        url: url,
        message: "Task completed successfully"
      };
    } else if (status === "failed" || status === "error" || status === "fail") {
      // Extract error message from poll result
      const errorMessage = pollResult.failMsg || pollResult.message || pollResult.data?.failMsg || pollResult.data?.message || "Generation failed";

      // Use RPC for failed status too
      const { error: updateError } = await (supabase.rpc as any)('update_generation_log_status', {
        p_log_id: logId,
        p_user_id: log.user_id,
        p_status: "failed",
        p_result_url: null,
        p_completed_at: new Date().toISOString()
      });

      if (updateError) {
        await supabase
          .from("generation_logs")
          .update({
            status: "failed",
            error_message: errorMessage
          })
          .eq("id", logId);
      } else {
        // Also update error_message separately since RPC might not support it
        await supabase
          .from("generation_logs")
          .update({ error_message: errorMessage })
          .eq("id", logId);
      }

      return {
        success: false,
        status: "failed",
        message: errorMessage
      };
    } else {
      // Still processing
      // Update DB to processing if it was pending
      if (log.status === "pending") {
        await (supabase.rpc as any)('update_generation_log_status', {
          p_log_id: logId,
          p_user_id: log.user_id,
          p_status: "processing",
          p_result_url: log.result_url, // Keep existing result_url (taskId)
          p_completed_at: null
        });
      }

      return {
        success: true,
        status: "processing",
        message: `Task is still ${status}`
      };
    }
  } catch (error: any) {
    console.error("Error checking task status:", error);
    return {
      success: false,
      status: "error",
      message: error.message || "Failed to check status"
    };
  }
}

/**
 * Retry fetching result for a specific taskId
 */
export async function retryFetchResult(taskId: string) {
  try {
    const apiKey = await getKieApiKey();
    const pollResult = await pollKieTask(taskId, apiKey, "market");

    const status = pollResult.state || pollResult.data?.state;
    let url = null;

    const resultJson = pollResult.resultJson || pollResult.data?.resultJson;
    if (resultJson) {
      try {
        const parsed = typeof resultJson === 'string' ? JSON.parse(resultJson) : resultJson;
        url = parsed.resultUrls?.[0] || parsed.url;
      } catch (e) {
        if (typeof resultJson === 'string' && resultJson.startsWith('http')) {
          url = resultJson;
        }
      }
    }

    if (status === "success" || url) {
      return {
        success: true,
        status: "completed",
        url: url
      };
    } else if (status === "failed" || status === "error") {
      return {
        success: false,
        status: "failed",
        message: pollResult.message || "Generation failed"
      };
    } else {
      return {
        success: true,
        status: "processing"
      };
    }
  } catch (error: any) {
    return {
      success: false,
      status: "error",
      message: error.message
    };
  }
}
